// src/components/leader/ChoreographyChecklist.js
// "Co zbývá" - what is done and what is missing for one choreography (audit FL-20, §5.1).
// Every row has an icon AND a text state, never colour alone.
import React from 'react';
import { CheckCircle2, Circle, MinusCircle, XCircle } from 'lucide-react';
import { PAYMENT_STATUS } from '../../constants/categories';
import { formatDate, countLabel } from '../../utils/format';
import { MIN_DANCERS, MAX_DANCERS } from './DancerPicker';

const DANCER_FORMS = ['tanečník', 'tanečníci', 'tanečníků'];

export function checklistItems(choreography) {
  if (!choreography) return [];
  const status = choreography.status;
  const isFinale = choreography.competition?.type === 'grand_finale' || choreography.competition?.isGrandFinale;
  const dancerCount = (choreography.dancers || []).length;
  const confirmed = ['confirmed', 'approved'].includes(status);

  const items = [
    {
      key: 'registration',
      label: 'Registrace',
      state: status === 'pre_registered' ? 'todo' : 'done',
      detail: status === 'pre_registered' ? 'Předregistrace – po otevření registrace ji zaregistrujte' : 'Zaregistrováno',
    },
    {
      key: 'dancers',
      label: 'Tanečníci',
      state: dancerCount >= MIN_DANCERS && dancerCount <= MAX_DANCERS ? 'done' : 'todo',
      detail:
        dancerCount >= MIN_DANCERS
          ? countLabel(dancerCount, DANCER_FORMS)
          : `${countLabel(dancerCount, DANCER_FORMS)} – potřeba alespoň ${MIN_DANCERS}`,
    },
    {
      key: 'music',
      label: 'Hudba',
      state: choreography.musicFile ? 'done' : 'todo',
      detail: choreography.musicFile ? 'Nahraná' : 'Chybí',
    },
    isFinale
      ? {
          key: 'video',
          label: 'Video pozadí',
          state: choreography.finaleBackgroundVideo ? 'done' : 'optional',
          detail: choreography.finaleBackgroundVideo ? 'Nahrané' : 'Nepovinné',
        }
      : {
          key: 'video',
          label: 'Ověřovací video',
          state: choreography.verificationVideo ? 'done' : 'optional',
          detail: choreography.verificationVideo ? 'Nahrané' : 'Nepovinné, ale doporučené',
        },
    {
      key: 'presence',
      label: 'Prezence',
      state: status === 'rejected' ? 'failed' : confirmed ? 'done' : 'todo',
      detail:
        status === 'rejected'
          ? 'Zamítnuto organizátorem'
          : confirmed
          ? `Potvrzena${choreography.confirmedAt ? ` ${formatDate(choreography.confirmedAt)}` : ''}`
          : 'Nepotvrzena',
    },
  ];

  // Payment only matters once presence is confirmed (only then can it be invoiced), and never
  // for a free competition.
  const payment = choreography.paymentStatus || choreography.payment_status;
  if (payment && confirmed && !choreography.competition?.isFree) {
    items.push({
      key: 'payment',
      label: 'Platba',
      state: payment === 'paid' || payment === 'waived' ? 'done' : 'todo',
      detail: PAYMENT_STATUS[payment]?.label || payment,
    });
  }
  return items;
}

const ICONS = {
  done: { Icon: CheckCircle2, className: 'text-green-600', sr: 'hotovo' },
  todo: { Icon: Circle, className: 'text-amber-600', sr: 'zbývá' },
  optional: { Icon: MinusCircle, className: 'text-gray-400', sr: 'nepovinné' },
  failed: { Icon: XCircle, className: 'text-red-600', sr: 'problém' },
};

export default function ChoreographyChecklist({ choreography }) {
  const items = checklistItems(choreography);
  return (
    <ul className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
      {items.map((item) => {
        const { Icon, className, sr } = ICONS[item.state];
        return (
          <li key={item.key} className="flex items-start gap-2 text-sm">
            <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${className}`} aria-hidden="true" />
            <span>
              <span className="font-medium text-gray-900">{item.label}</span>
              <span className="sr-only"> ({sr})</span>
              <span className="text-gray-600"> – {item.detail}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
